// © Microsoft Corporation. All rights reserved.

import { getLocalDevPrefix, useLocalConfigs } from "../../utilities";

import { ConfigOrigins } from "@msnews/core-contracts";
import { ExperienceConfig } from "@msnews/config-data-types";
import { fetchAsync } from "./BlobFetcher";
import { memoize } from "lodash-es";

/**
 * ConfigService class
 * This is for fetching and parsing the Config into an easily consumable form.
 */
export class ConfigService {
    /** Array for keeping track of the null fetches */
    private failedConfigFetches: string[] = [];

    /** Application Type */
    private appType: string;

    /** Indicates if configs should be served from local repo */
    private configOrigins: ConfigOrigins;

    /**
     * Initialize config service.
     * @param {appType} appType
     * @param {ConfigOrigins} Indicates if configs should be served from local repo
     */
    constructor(appType: string, configOrigins: ConfigOrigins) {
        this.appType = appType;
        this.configOrigins = configOrigins;
        this.fetchAndParseConfig = memoize(this.fetchAndParseConfig, (...args) => args.join("/"));
    }

    /**
     * This function fetches and parses config file from azure blob storage.
     * @param {string} configType config type, loc-configs or feature-configs
     * @param {string} configReference config reference: combination of experienceType, instance source and shared namespace.
     * @param {string} configFileName config src file name.
     * @param {string} hashedFileName config hashed file name from the manifest file.
     */
    public async fetchAndParseConfig(configType: string, configReference: string, configFileName: string, hashedFileName: string): Promise<ExperienceConfig> {
        // In prod scenario, we don't proceed if hashedFileName is empty
        // In local scenario, hashed name could be empty
        // If hashed file name is null/undefined AND it's not local scenario, return undefined
        const isLocalConfigs = useLocalConfigs(configType, this.configOrigins);
        if (!hashedFileName && !isLocalConfigs) {
            return undefined;
        }

        const relativeUrl = isLocalConfigs
            ? `${getLocalDevPrefix(configType, configReference, this.appType)}/${configFileName}`
            : `${configType}/${configReference}/${configFileName}/${hashedFileName}`;

        try {
            return await fetchAsync(relativeUrl, configType, this.configOrigins);
        } catch (error) {
            if (!isLocalConfigs) {
                this.failedConfigFetches.push(error);
            }
        }
    }

    /**
     * Returns the list of failed config fetches.
     */
    public getFailedConfigFetches(): string[] {
        return this.failedConfigFetches;
    }
}